import React, { useEffect, useRef, useState } from 'react';

interface ImageBlendCanvasProps {
    imageA:               string;
    imageB:               string;
    duration?:            number;
    width?:               number;
    height?:              number;
    loop?:                boolean;
    onAnimationComplete?: () => void;
    className?:           string;
}

export const ImageBlendCanvas: React.FC<ImageBlendCanvasProps> = ( {
    imageA,
    imageB,
    duration = 2000,
    width = 800,
    height = 600,
    loop = true,
    onAnimationComplete,
    className = ""
} ) => {
    const canvasRef    = useRef<HTMLCanvasElement>( null );
    const animationRef = useRef<number>();
    const [isLoading, setIsLoading] = useState( true );
    const [error,     setError    ] = useState<string | null>( null );

    useEffect( () => {
        const canvas = canvasRef.current;
        if( !canvas ) return;

        const ctx = canvas.getContext( '2d' );
        if( !ctx ) return;

        let imgA: HTMLImageElement;
        let imgB: HTMLImageElement;
        let startTime: number;
        let isForward = true;

        // Load images
        const loadImages = async () => {
            try {
                setIsLoading( true );
                setError( null );

                // Create and load first image
                imgA = new Image();
                const imgALoaded = new Promise( ( resolve, reject ) => {
                    imgA.onload = resolve;
                    imgA.onerror = reject;
                } );
                imgA.src = imageA;

                // Create and load second image
                imgB = new Image();
                const imgBLoaded = new Promise( ( resolve, reject ) => {
                    imgB.onload = resolve;
                    imgB.onerror = reject;
                } );
                imgB.src = imageB;

                // Wait for both images to load
                await Promise.all( [imgALoaded, imgBLoaded] );

                setIsLoading( false );

                // Start animation
                startTime = performance.now();
                animate();
            } catch( err ) {
                setError( 'Failed to load images' );
                setIsLoading( false );
            }
        };

        const animate = () => {
            const currentTime = performance.now();
            const elapsed = currentTime - startTime;

            // Calculate progress (0 to 1)
            let progress = Math.min( elapsed / duration, 1 );

            // If looping, reverse direction at the end
            if( loop && progress === 1 ) {
                isForward = !isForward;
                startTime = currentTime;
                progress = 0;
            }

            // Apply easing function (smooth in/out)
            const easedProgress = isForward
                ? easeInOutCubic( progress )
                : easeInOutCubic( 1 - progress );

            // Clear canvas
            ctx.clearRect( 0, 0, canvas.width, canvas.height );

            ctx.imageSmoothingEnabled = true;
            ( ctx as any ).webkitImageSmoothingEnabled = true;
            ( ctx as any ).mozImageSmoothingEnabled = true;
            ( ctx as any ).msImageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";

            // Calculate dimensions to fit images while maintaining aspect ratio
            const { drawWidth, drawHeight, offsetX, offsetY } = calculateDrawDimensions(
                imgA,
                canvas.width,
                canvas.height
            );

            // Draw first image
            ctx.globalAlpha = 1;
            ctx.drawImage( imgA, offsetX, offsetY, drawWidth, drawHeight );

            // Draw second image with blend
            ctx.globalAlpha = easedProgress;
            ctx.drawImage( imgB, offsetX, offsetY, drawWidth, drawHeight );

            // Reset alpha
            ctx.globalAlpha = 1;

            // Continue animation
            if( progress < 1 || loop ) {
                animationRef.current = requestAnimationFrame( animate );
            } else {
                onAnimationComplete?.();
            }
        };

        loadImages();

        // Cleanup
        return () => {
            if( animationRef.current ) {
                cancelAnimationFrame( animationRef.current );
            }
        };
    }, [imageA, imageB, duration, loop, onAnimationComplete] );

    // Easing function for smooth animation
    const easeInOutCubic = ( t: number ): number => {
        return t < 0.5
            ? 4 * t * t * t
            : 1 - Math.pow( -2 * t + 2, 3 ) / 2;
    };

    // Calculate dimensions to fit image in canvas while maintaining aspect ratio
    const calculateDrawDimensions = (
        img: HTMLImageElement,
        canvasWidth: number,
        canvasHeight: number
    ) => {
        const imgAspect = img.width / img.height;
        const canvasAspect = canvasWidth / canvasHeight;

        let drawWidth, drawHeight, offsetX, offsetY;
        if( imgAspect > canvasAspect ) {
            // Image is wider than canvas
            drawWidth = canvasWidth;
            drawHeight = canvasWidth / imgAspect;
            offsetX = 0;
            offsetY = ( canvasHeight - drawHeight ) / 2;
        } else {
            // Image is taller than canvas
            drawHeight = canvasHeight;
            drawWidth = canvasHeight * imgAspect;
            offsetX = ( canvasWidth - drawWidth ) / 2;
            offsetY = 0;
        }

        return { drawWidth, drawHeight, offsetX, offsetY };
    };

    return (
        <div style={{ position: 'relative', width, height }}>
            <canvas
                className={className}
                ref={canvasRef}
                width={width + "px"}
                height={height + "px"}
                style={{
                    display: 'block',
                    backgroundColor: '#000',
                }}
            />
        </div>
    );
};